import React, { useEffect, useRef } from "react";
import { cn } from "../../lib/utils";

/**
 * Scroll-in reveal, on an IntersectionObserver.
 *
 * The old site pulled in AOS and framer-motion to do this. An observer plus
 * one CSS class is the same effect in a dozen lines, with nothing to
 * initialise and nothing to keep in sync with a scroll position.
 *
 *   <Reveal>          fades up once, when it enters the viewport
 *   <Reveal stagger>  releases its direct children one after another
 */
export default function Reveal({
  as: Tag = "div",
  children,
  className,
  delay = 0,
  stagger = false,
  ...rest
}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const targets = stagger ? Array.from(el.children) : [el];
    targets.forEach((t, i) => {
      t.classList.add("reveal");
      t.style.transitionDelay = `${delay + (stagger ? i * 0.035 : 0)}s`;
    });

    /* Motion off: release everything immediately rather than leaving it at
       opacity 0 waiting for an animation that will never run. */
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      targets.forEach((t) => t.classList.add("in"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("in");
          /* Once seen, stop watching — a reveal that replays on every scroll
             past is a distraction, not an entrance. */
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.05 },
    );

    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [delay, stagger]);

  /* min-w-0 because a Reveal is nearly always a grid or flex item here, and
     those default to `min-width: auto` — one long unbreakable label inside
     then widens the whole track past the viewport instead of wrapping. That
     is what pushed the contact page 31px off the right edge of a phone. It is
     a class rather than something the observer sets, so it holds from the
     first paint. */
  return (
    <Tag ref={ref} className={cn("min-w-0", className)} {...rest}>
      {children}
    </Tag>
  );
}
